import styled, { useTheme } from 'styled-components';

/* ─────────────────────────── Bar chart (CSS) ─────────────────────────── */

export interface BarDatum {
  label: string;
  value: number;
  color: string;
}

interface BarChartProps {
  data: BarDatum[];
  height?: number;
}

export function BarChart({ data, height = 180 }: BarChartProps) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <BarWrap style={{ height }}>
      {data.map((d) => {
        const pct = (d.value / max) * 100;
        return (
          <BarCol key={d.label}>
            <BarValue>{d.value}</BarValue>
            <BarTrack>
              <BarFill
                style={{
                  height: `${d.value === 0 ? 0 : Math.max(pct, 3)}%`,
                  background: d.color,
                }}
              />
            </BarTrack>
            <BarLabel>{d.label}</BarLabel>
          </BarCol>
        );
      })}
    </BarWrap>
  );
}

const BarWrap = styled.div`
  display: flex;
  align-items: stretch;
  gap: 14px;
  padding-top: 8px;
`;

const BarCol = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const BarValue = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const BarTrack = styled.div`
  flex: 1;
  width: 100%;
  max-width: 56px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const BarFill = styled.div`
  width: 100%;
  border-radius: 12px 12px 4px 4px;
  min-height: 0;
  transition: height 0.4s ease;
`;

const BarLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;

/* ─────────────────────────── Line chart (SVG) ─────────────────────────── */

export interface LineSeries {
  name: string;
  color: string;
  values: number[];
}

interface LineChartProps {
  labels: string[];
  series: LineSeries[];
  label?: string;
}

const VB_W = 560;
const VB_H = 200;
const PAD_L = 34;
const PAD_R = 12;
const PAD_T = 16;
const PAD_B = 28;

export function LineChart({ labels, series, label = '기간별 추이' }: LineChartProps) {
  const theme = useTheme();
  const grid = theme.colors.border;
  const muted = theme.colors.textMuted;
  const pointFill = theme.colors.surface;
  const innerW = VB_W - PAD_L - PAD_R;
  const innerH = VB_H - PAD_T - PAD_B;
  const n = labels.length;

  const maxValue = Math.max(
    1,
    ...series.flatMap((s) => s.values)
  );
  // y축 눈금을 보기 좋은 값으로 반올림
  const step = niceStep(maxValue);
  const yMax = Math.max(step, Math.ceil(maxValue / step) * step);
  const ticks = Math.round(yMax / step);

  const xFor = (i: number) =>
    n <= 1 ? PAD_L + innerW / 2 : PAD_L + (innerW * i) / (n - 1);
  const yFor = (v: number) => PAD_T + innerH - (v / yMax) * innerH;

  // x축 라벨은 과밀 방지를 위해 일부만 표시
  const labelEvery = Math.max(1, Math.ceil(n / 7));

  return (
    <LineWrap>
      <Legend>
        {series.map((s) => (
          <LegendItem key={s.name}>
            <LegendDot style={{ background: s.color }} />
            {s.name}
          </LegendItem>
        ))}
      </Legend>
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={`${label}: ${series.map((s) => s.name).join(', ')}`}
      >
        <desc>
          {labels.length > 0 ? `${labels[0]}부터 ${labels[labels.length - 1]}까지. ` : ''}
          {series.map((s) => `${s.name}: ${s.values.map((value, index) => `${labels[index] ?? index + 1} ${value.toLocaleString('ko-KR')}`).join(', ')}`).join('. ')}
        </desc>
        {/* 가로 그리드 + y축 눈금 */}
        {Array.from({ length: ticks + 1 }, (_, t) => {
          const value = (yMax / ticks) * t;
          const y = yFor(value);
          return (
            <g key={t}>
              <line
                x1={PAD_L}
                y1={y}
                x2={VB_W - PAD_R}
                y2={y}
                stroke={grid}
                strokeWidth={1}
              />
              <text
                x={PAD_L - 6}
                y={y + 3}
                textAnchor="end"
                fontSize={10}
                fill={muted}
              >
                {Math.round(value)}
              </text>
            </g>
          );
        })}

        {/* x축 라벨 */}
        {labels.map((label, i) =>
          i % labelEvery === 0 || i === n - 1 ? (
            <text
              key={label + i}
              x={xFor(i)}
              y={VB_H - 10}
              textAnchor="middle"
              fontSize={10}
              fill={muted}
            >
              {label}
            </text>
          ) : null
        )}

        {/* 시리즈 라인 + 점 */}
        {series.map((s) => {
          const points = s.values.map((v, i) => `${xFor(i)},${yFor(v)}`).join(' ');
          return (
            <g key={s.name}>
              <polyline
                points={points}
                fill="none"
                stroke={s.color}
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {s.values.map((v, i) => (
                <circle
                  key={i}
                  cx={xFor(i)}
                  cy={yFor(v)}
                  r={2.6}
                  fill={pointFill}
                  stroke={s.color}
                  strokeWidth={2}
                />
              ))}
            </g>
          );
        })}
      </svg>
    </LineWrap>
  );
}

function niceStep(max: number): number {
  if (max <= 5) return 1;
  if (max <= 10) return 2;
  if (max <= 25) return 5;
  if (max <= 50) return 10;
  if (max <= 100) return 20;
  const pow = Math.pow(10, Math.floor(Math.log10(max)));
  return pow / 2;
}

const LineWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Legend = styled.div`
  display: flex;
  gap: 16px;
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const LegendDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 3px;
`;
